import {
  Box,
  Badge,
  Card,
  Group,
  rem,
  Text,
  Image,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconPackage,
  IconTrash,
  IconEdit,
  IconPlus,
  IconMinus,
} from "@tabler/icons-react";

export type ItemRarity = "common" | "uncommon" | "rare" | "epic" | "legendary";

const rarityConfig: Record<ItemRarity, { color: string; label: string }> = {
  common: {
    color: "gray",
    label: "Common",
  },
  uncommon: {
    color: "green",
    label: "Uncommon",
  },
  rare: {
    color: "blue",
    label: "Rare",
  },
  epic: {
    color: "violet",
    label: "Epic",
  },
  legendary: {
    color: "orange",
    label: "Legendary",
  },
};

const useStyles = createStyles((theme, { rarity }: { rarity: ItemRarity }) => {
  const config = rarityConfig[rarity];

  return {
    card: {
      backgroundColor: theme.colors.dark[6],
      border: `1px solid ${theme.colors.dark[4]}`,
      borderRadius: theme.radius.md,
      overflow: "hidden",
      transition: "border-color 200ms ease",
      position: "relative",
      "&:hover": {
        borderColor: theme.colors[config.color][6],
      },
    },
    cardLegendary: {
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: rem(3),
        backgroundColor: theme.colors[config.color][6],
      },
    },
    imageContainer: {
      position: "relative",
      backgroundColor: theme.colors.dark[7],
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: rem(120),
      overflow: "hidden",
    },
    imagePlaceholder: {
      width: rem(64),
      height: rem(64),
      borderRadius: theme.radius.md,
      backgroundColor: theme.colors.dark[5],
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: theme.colors.dark[3],
    },
    content: {
      padding: theme.spacing.sm,
    },
    name: {
      fontSize: theme.fontSizes.md,
      fontWeight: 600,
      color: theme.colors.dark[0],
      lineHeight: 1.2,
    },
    description: {
      fontSize: theme.fontSizes.xs,
      color: theme.colors.dark[3],
      marginTop: rem(4),
      lineHeight: 1.4,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    footer: {
      padding: theme.spacing.sm,
      paddingTop: 0,
    },
    quantity: {
      fontSize: theme.fontSizes.sm,
      fontWeight: 700,
      color: theme.colors.cyan[4],
    },
    quantityLabel: {
      fontSize: theme.fontSizes.xs,
      color: theme.colors.dark[3],
    },
    actions: {
      display: "flex",
      gap: rem(4),
    },
    actionButton: {
      backgroundColor: theme.colors.dark[5],
      border: "none",
      "&:hover": {
        backgroundColor: theme.colors.dark[4],
      },
    },
    rarityBadge: {
      position: "absolute",
      top: rem(8),
      right: rem(8),
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.03em",
      fontSize: rem(10),
    },
    categoryBadge: {
      backgroundColor: `${theme.colors.dark[5]}`,
      color: theme.colors.dark[1],
    },
  };
});

export interface ItemCardProps {
  id: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  category?: string | null;
  quantity?: number;
  rarity?: ItemRarity;
  showQuantityControls?: boolean;
  showActions?: boolean;
  onQuantityChange?: (id: string, delta: number) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onClick?: (id: string) => void;
}

export const ItemCard = ({
  id,
  name,
  description,
  imageUrl,
  category,
  quantity,
  rarity = "common",
  showQuantityControls = false,
  showActions = false,
  onQuantityChange,
  onEdit,
  onDelete,
  onClick,
}: ItemCardProps) => {
  const { classes, cx } = useStyles({ rarity });
  const config = rarityConfig[rarity];

  return (
    <Card
      className={cx(classes.card, {
        [classes.cardLegendary]: rarity === "legendary" || rarity === "epic",
      })}
      p={0}
      onClick={() => onClick?.(id)}
      style={{ cursor: onClick ? "pointer" : "default" }}
    >
      {/* Image Container */}
      <Box className={classes.imageContainer}>
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={name}
            height={120}
            style={{ objectFit: "cover" }}
            fallbackSrc=""
          />
        ) : (
          <Box className={classes.imagePlaceholder}>
            <IconPackage size={32} />
          </Box>
        )}
        <Badge
          className={classes.rarityBadge}
          color={config.color}
          variant="filled"
          size="xs"
        >
          {config.label}
        </Badge>
      </Box>

      {/* Content */}
      <Box className={classes.content}>
        <Text className={classes.name}>{name}</Text>
        {description && (
          <Text className={classes.description}>{description}</Text>
        )}
        {category && (
          <Badge
            className={classes.categoryBadge}
            variant="light"
            size="xs"
            mt="xs"
          >
            {category}
          </Badge>
        )}
      </Box>

      {/* Footer */}
      <Box className={classes.footer}>
        <Group justify="space-between" align="center">
          {/* Quantity */}
          {quantity !== undefined && (
            <div>
              {showQuantityControls ? (
                <Group gap={4}>
                  <ActionIcon
                    className={classes.actionButton}
                    size="sm"
                    radius="md"
                    onClick={(e) => {
                      e.stopPropagation();
                      onQuantityChange?.(id, -1);
                    }}
                    disabled={quantity <= 0}
                  >
                    <IconMinus size={12} />
                  </ActionIcon>
                  <Text className={classes.quantity} px={8}>
                    {quantity}
                  </Text>
                  <ActionIcon
                    className={classes.actionButton}
                    size="sm"
                    radius="md"
                    onClick={(e) => {
                      e.stopPropagation();
                      onQuantityChange?.(id, 1);
                    }}
                  >
                    <IconPlus size={12} />
                  </ActionIcon>
                </Group>
              ) : (
                <div>
                  <Text className={classes.quantityLabel}>Quantity</Text>
                  <Text className={classes.quantity}>x{quantity}</Text>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          {showActions && (
            <Box className={classes.actions}>
              {onEdit && (
                <Tooltip label="Edit" position="top">
                  <ActionIcon
                    className={classes.actionButton}
                    size="sm"
                    radius="md"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(id);
                    }}
                  >
                    <IconEdit size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip label="Delete" position="top">
                  <ActionIcon
                    className={classes.actionButton}
                    size="sm"
                    radius="md"
                    color="rose"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(id);
                    }}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Box>
          )}
        </Group>
      </Box>
    </Card>
  );
};

// Compact variant for lists
export const ItemCardCompact = ({
  id,
  name,
  description,
  imageUrl,
  quantity,
  rarity = "common",
  showQuantityControls = false,
  onQuantityChange,
  onClick,
}: ItemCardProps) => {
  const config = rarityConfig[rarity];

  return (
    <Card
      p="xs"
      radius="md"
      style={{
        backgroundColor: "var(--mantine-color-dark-7)",
        border: `1px solid var(--mantine-color-dark-5)`,
        cursor: onClick ? "pointer" : "default",
      }}
      onClick={() => onClick?.(id)}
    >
      <Group gap="sm" wrap="nowrap">
        <Box
          style={{
            width: rem(40),
            height: rem(40),
            borderRadius: rem(8),
            backgroundColor: "var(--mantine-color-dark-6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            border: `2px solid var(--mantine-color-${config.color}-7)`,
          }}
        >
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={name}
              width={36}
              height={36}
              fit="cover"
              radius="sm"
            />
          ) : (
            <IconPackage size={20} color="gray" />
          )}
        </Box>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={600} lineClamp={1}>
            {name}
          </Text>
          {description && (
            <Text size="xs" c="dimmed" lineClamp={1}>
              {description}
            </Text>
          )}
        </Box>
        {quantity !== undefined && (
          <Group gap={4}>
            {showQuantityControls ? (
              <>
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuantityChange?.(id, -1);
                  }}
                  disabled={quantity <= 0}
                >
                  <IconMinus size={12} />
                </ActionIcon>
                <Text
                  size="sm"
                  fw={700}
                  style={{ minWidth: rem(24), textAlign: "center" }}
                >
                  {quantity}
                </Text>
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuantityChange?.(id, 1);
                  }}
                >
                  <IconPlus size={12} />
                </ActionIcon>
              </>
            ) : (
              <Badge color={config.color} variant="light" size="sm">
                x{quantity}
              </Badge>
            )}
          </Group>
        )}
      </Group>
    </Card>
  );
};

export default ItemCard;
